import {VEHICLE_SEARCH_MIN_LENGTH} from "./vehicle-search";

export type VehicleListFilterArgs = {
  permitSelection: boolean;
  forHouseholdId: number | null;
  houseHoldId: number | null;
  contextPropertyId: number | null | undefined;
  propertyId: number | null | undefined;
  extraFilter?: Record<string, number | string | undefined>;
  searchTerm?: string;
};

function searchFilter(searchTerm?: string): Record<string, string> {
  const q = searchTerm?.trim() ?? "";
  if (q.length >= VEHICLE_SEARCH_MIN_LENGTH) {
    return {q};
  }
  return {};
}

/**
 * Builds GET /api/v1/vehicles filter params for combobox scope modes.
 * Permit mode never sends hard `house_hold_id` (hides unknown vehicles).
 */
export function buildVehicleListFilter({
  permitSelection,
  forHouseholdId,
  houseHoldId,
  contextPropertyId,
  propertyId,
  extraFilter = {},
  searchTerm,
}: VehicleListFilterArgs): Record<string, number | string | undefined> {
  const qFilter = searchFilter(searchTerm);

  if (permitSelection) {
    const base: Record<string, number | string | undefined> = {...extraFilter};
    if (contextPropertyId != null && contextPropertyId > 0) {
      base.context_property_id = contextPropertyId;
    }
    if (forHouseholdId != null && forHouseholdId > 0) {
      base.for_household_id = forHouseholdId;
    }
    return {...base, ...qFilter};
  }

  if (houseHoldId) {
    return {house_hold_id: houseHoldId, ...extraFilter, ...qFilter};
  }

  if (contextPropertyId != null && contextPropertyId > 0) {
    return {context_property_id: contextPropertyId, ...extraFilter, ...qFilter};
  }

  if (propertyId != null && propertyId > 0) {
    return {property_id: propertyId, ...extraFilter, ...qFilter};
  }

  return {...extraFilter, ...qFilter};
}

export function isVehicleListScopeOk(args: {
  permitSelection: boolean;
  forHouseholdId: number | null;
  houseHoldId: number | null;
  contextPropertyId: number | null | undefined;
  propertyId: number | null | undefined;
  contextPropertyIdPropDefined: boolean;
}): boolean {
  const {
    permitSelection,
    forHouseholdId,
    houseHoldId,
    contextPropertyId,
    propertyId,
    contextPropertyIdPropDefined,
  } = args;

  const propertyScope =
    contextPropertyId !== undefined && contextPropertyId != null && contextPropertyId > 0;
  const hasForHousehold = forHouseholdId != null && forHouseholdId > 0;

  if (permitSelection) {
    return propertyScope && hasForHousehold;
  }

  if (houseHoldId) {
    return true;
  }

  if (propertyScope) {
    return true;
  }

  if (propertyId != null && propertyId > 0) {
    return true;
  }

  // Legacy: contextPropertyId explicitly passed as null still means "require property"
  if (contextPropertyIdPropDefined && !propertyScope) {
    return false;
  }

  return false;
}

export function vehicleListPlaceholder(scopeOk: boolean, args: {
  permitSelection: boolean;
  contextPropertyId: number | null | undefined;
  contextPropertyIdPropDefined: boolean;
}): string {
  if (scopeOk) {
    return "Select vehicle";
  }

  const {permitSelection, contextPropertyId, contextPropertyIdPropDefined} = args;
  const propertyScope =
    contextPropertyId !== undefined && contextPropertyId != null && contextPropertyId > 0;

  if (permitSelection) {
    return propertyScope ? "Select household first" : "Select property first";
  }

  if (contextPropertyIdPropDefined) {
    return "Select property first";
  }

  return "Select household first";
}
